import { Navigate, useNavigate, useParams } from "react-router-dom";
import { TopBar } from "./TopBar";
import { PageFooter } from "./PageFooter";
import { useIntakeCounts } from "./IntakeCountsContext";
import { useAppUser } from "./AppUserContext";
import { SpendLane } from "../lanes/spend/SpendLane";
import { NomLane } from "../lanes/nom/NomLane";
import { SpotLane } from "../lanes/spot/SpotLane";
import { TdsLane } from "../lanes/tds/TdsLane";
import { Btn } from "../components/ui/Button";
import { PATHS } from "./paths";

const LANES = [
  { key: "spend", label: "Client Spend Data", icon: "table_view" },
  { key: "nom", label: "Price Nominations", icon: "description" },
  { key: "spot", label: "Spot Nominations", icon: "bolt" },
  { key: "tds", label: "Technical Data Sheets", icon: "lab_profile" },
] as const;
type LaneKey = (typeof LANES)[number]["key"];
const LANE_KEYS = LANES.map((l) => l.key) as readonly string[];

const ENV_LABEL = import.meta.env.VITE_ENV_LABEL ?? (import.meta.env.DEV ? "Development" : "Production");

function Counter({ n, label }: { n: number; label: string }) {
  return (
    <div className="text-right">
      <div className="text-[22px] font-semibold leading-none bg-[linear-gradient(180deg,#8ff453,#64b37a)] bg-clip-text text-transparent">{n}</div>
      <div className="text-[11px] text-rs-light-gray mt-[3px]">{label}</div>
    </div>
  );
}

export function Shell() {
  const { lane: laneParam } = useParams<{ lane: string }>();
  const navigate = useNavigate();
  const { lettersInQueue, fieldsToReview, lettersCommitted, spotQuotesLogged } = useIntakeCounts();
  const { appRole } = useAppUser();

  if (!laneParam || !LANE_KEYS.includes(laneParam)) {
    return <Navigate to={PATHS.intakeLane("spend")} replace />;
  }
  const lane = laneParam as LaneKey;

  return (
    <>
      <TopBar title="RESIN8 Data Intake" envLabel={ENV_LABEL}>
        <Counter n={lettersInQueue} label="Letters in queue" />
        <Counter n={fieldsToReview} label="Fields to review" />
        <Counter n={lettersCommitted} label="Letters committed" />
        <Counter n={spotQuotesLogged} label="Spot quotes logged" />
        <Btn variant="ghost" onClick={() => navigate(PATHS.data)}>
          <span className="material-symbols-sharp">folder_shared</span>Resin8 Data
        </Btn>
        <Btn variant="ghost" onClick={() => navigate(PATHS.cockpit)}>
          <span className="material-symbols-sharp">monitoring</span>Consultant Cockpit
        </Btn>
        <Btn variant="ghost" onClick={() => navigate(PATHS.plantRegistry)}>
          <span className="material-symbols-sharp">factory</span>Plant Registry
        </Btn>
        {appRole === "Admin" && (
          <Btn variant="ghost" onClick={() => navigate(PATHS.adminUsers)}>
            <span className="material-symbols-sharp">admin_panel_settings</span>Users
          </Btn>
        )}
      </TopBar>

      <nav className="flex bg-rs-black px-7 border-b border-rs-jet sticky top-[59px] z-[19] overflow-x-auto" role="tablist" aria-label="Intake type">
        {LANES.map((l) => (
          <button
            key={l.key}
            className={`bg-transparent border-none border-b-2 py-[13px] mr-[30px] whitespace-nowrap flex items-center gap-2 ${
              lane === l.key ? "text-white border-b-rs-green" : "text-rs-light-gray border-b-transparent hover:text-rs-off-white"
            }`}
            role="tab"
            aria-selected={lane === l.key}
            aria-controls={`lane-${l.key}`}
            onClick={() => navigate(PATHS.intakeLane(l.key))}
          >
            <span className="material-symbols-sharp">{l.icon}</span>
            {l.label}
            {l.key === "nom" && (
              <span
                className={`text-[11px] rounded-[10px] py-px px-[7px] ${
                  lane === l.key ? "bg-rs-green text-rs-black" : "bg-rs-jet text-rs-light-gray"
                }`}
              >
                {lettersInQueue}
              </span>
            )}
          </button>
        ))}
      </nav>

      <main className="pt-[26px] px-7 pb-[70px]">
        {lane === "spend" && <SpendLane />}
        {lane === "nom" && <NomLane />}
        {lane === "spot" && <SpotLane />}
        {lane === "tds" && <TdsLane />}
      </main>

      <PageFooter />
    </>
  );
}
